import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChannelKind, Thread } from '@prody/shared';
import { CHANNEL_CAPABILITIES, canSendFreeform } from '@prody/shared';
import {
  Button, CHANNEL_ICONS, Code, EmptyState, IconWarn, ListPane, PageHeader,
  Stamp, Textarea, Toolbar, Workbench, toast,
} from '~/design-system';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useMessages, useSendMessage, useThreads } from '~/platform/data/queries';
import './messaging.css';

/* ==================================================================
   One inbox, every channel.

   The model is a thread per *contact*, not per channel. A salesperson
   wants "everything we've said to Sorlí" — the fact that half of it
   arrived on WhatsApp and half by email is a property of individual
   messages, not an organising principle.

   The composer is channel-aware because the channels are not
   interchangeable. WhatsApp closes its free-form window 24 hours
   after the customer's last message and only approved templates may
   be sent after that; Telegram has no such rule. Rather than letting
   a send fail at the API, the composer reads the capability table in
   @prody/shared and says so up front.
   ================================================================== */

export default function MessagingPage() {
  const { t } = useTranslation(['messaging', 'common']);
  const { business, format } = useBusiness();
  const { data: threads = [], isLoading } = useThreads(business.id);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedId && threads[0]) setSelectedId(threads[0].id);
  }, [threads, selectedId]);

  const selected = threads.find((thread) => thread.id === selectedId) ?? null;
  const unread = threads.reduce((sum, thread) => sum + thread.unreadCount, 0);

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={unread > 0 ? t('unread', { count: unread }) : undefined}
      />

      <Toolbar />

      <ListPane>
        <div className="pd-inbox">
          <ul className="pd-inbox__threads scroll-y">
            {!isLoading && threads.length === 0 ? (
              <li>
                <EmptyState compact title={t('empty')} body={t('emptyBody')} />
              </li>
            ) : null}

            {threads.map((thread) => (
              <li key={thread.id}>
                <ThreadRow
                  thread={thread}
                  active={thread.id === selectedId}
                  onSelect={() => setSelectedId(thread.id)}
                  relative={format.relative(thread.lastMessageAt)}
                />
              </li>
            ))}
          </ul>

          {selected ? (
            <Conversation thread={selected} />
          ) : (
            <div className="pd-inbox__blank">
              <EmptyState title={t('empty')} body={t('emptyBody')} />
            </div>
          )}
        </div>
      </ListPane>
    </Workbench>
  );
}

function ThreadRow({
  thread,
  active,
  onSelect,
  relative,
}: {
  thread: Thread;
  active: boolean;
  onSelect: () => void;
  relative: string;
}) {
  const Icon = CHANNEL_ICONS[thread.lastMessageChannel];
  return (
    <button
      type="button"
      className="pd-thread"
      data-active={active || undefined}
      data-unread={thread.unreadCount > 0 || undefined}
      onClick={onSelect}
    >
      <span className="pd-band" aria-hidden="true" />
      <span className="pd-thread__body">
        <span className="pd-thread__top">
          <span className="pd-thread__name truncate">{thread.partyName}</span>
          <span className="num pd-thread__time">{relative}</span>
        </span>
        <span className="pd-thread__preview truncate">{thread.lastMessagePreview}</span>
        <span className="pd-thread__channels">
          <Icon />
          {thread.unreadCount > 0 ? (
            <Stamp tone="accent" size="sm" dot>
              {thread.unreadCount}
            </Stamp>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function Conversation({ thread }: { thread: Thread }) {
  const { t } = useTranslation(['messaging', 'common']);
  const { business, format } = useBusiness();
  const { data: messages = [] } = useMessages(business.id, thread.id);
  const send = useSendMessage(business.id, thread.id);

  const [channel, setChannel] = useState<ChannelKind>(thread.lastMessageChannel);
  const [draft, setDraft] = useState('');

  const lastInboundAt = useMemo(
    () =>
      messages
        .filter((message) => message.direction === 'in' && message.channel === channel)
        .at(-1)?.sentAt ?? null,
    [messages, channel],
  );

  const caps = CHANNEL_CAPABILITIES[channel];
  const freeform = canSendFreeform(channel, lastInboundAt, Date.now());

  const submit = () => {
    if (!draft.trim()) return;
    send.mutate(
      { body: draft.trim(), channel },
      {
        onSuccess: () => {
          setDraft('');
          toast.ok(t('sent'), { detail: t(`common:channel.${channel}`) });
        },
        onError: () => toast.error(t('common:errors.saveFailed')),
      },
    );
  };

  return (
    <section className="pd-convo" aria-label={thread.partyName}>
      <header className="pd-convo__head">
        <div>
          <h2 className="pd-convo__name">{thread.partyName}</h2>
          <p className="t-small dim">{format.dateTime(thread.lastMessageAt, 'full')}</p>
        </div>
        <div className="row">
          <Button variant="quiet" size="sm">{t('snooze')}</Button>
          <Button variant="quiet" size="sm">{t('closeThread')}</Button>
        </div>
      </header>

      <div className="pd-convo__stream scroll-y">
        {messages.map((message) => {
          const Icon = CHANNEL_ICONS[message.channel];
          return (
            <article key={message.id} className="pd-msg" data-direction={message.direction}>
              <div className="pd-msg__meta">
                <Icon />
                <span className="stencil">{message.authorName ?? thread.partyName}</span>
                <span className="num faint">{format.dateTime(message.sentAt, 'time')}</span>
                {message.relatedOrderId ? <Code>{message.relatedOrderId}</Code> : null}
              </div>
              <p className="pd-msg__body">{message.body}</p>
              {message.transcript && message.transcript !== message.body ? (
                <p className="pd-msg__transcript">
                  <span className="stencil">{t('transcript')}</span>
                  {message.transcript}
                </p>
              ) : null}
            </article>
          );
        })}
      </div>

      <footer className="pd-convo__composer">
        {/* Channel switch. Only channels this contact is actually
            reachable on are offered. */}
        <div className="pd-convo__channels">
          {thread.channels.map((kind) => {
            const Icon = CHANNEL_ICONS[kind];
            return (
              <button
                key={kind}
                type="button"
                className="pd-convo__channel"
                data-active={kind === channel || undefined}
                onClick={() => setChannel(kind)}
                aria-pressed={kind === channel}
              >
                <Icon />
                <span>{t(`common:channel.${kind}`)}</span>
              </button>
            );
          })}
        </div>

        {!freeform ? (
          <p className="pd-convo__locked">
            <IconWarn />
            <span>
              <strong>{t('windowClosed', { channel: t(`common:channel.${channel}`) })}</strong>
              <br />
              {t('windowClosedBody', {
                channel: t(`common:channel.${channel}`),
                name: thread.partyName,
              })}
            </span>
            <Button variant="default" size="sm">
              {t('useTemplate')}
            </Button>
          </p>
        ) : (
          <div className="pd-convo__input">
            <Textarea
              rows={2}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('composerPlaceholder', {
                name: thread.partyName,
                channel: t(`common:channel.${channel}`),
              })}
              maxLength={caps.maxLength ?? undefined}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
              }}
            />
            <div className="pd-convo__send">
              {caps.maxLength ? (
                <span className="num faint">
                  {draft.length} / {caps.maxLength}
                </span>
              ) : null}
              <Button variant="primary" busy={send.isPending} onClick={submit} disabled={!draft.trim()}>
                {t('send')}
              </Button>
            </div>
          </div>
        )}
      </footer>
    </section>
  );
}
